import { useCallback, useEffect, useRef } from "react";
import { isLoggedIn } from "api/coachAuth";
import useTaskEditorStore from "hooks/useTaskEditorStore";
import { useRecoilValue, useSetRecoilState } from "recoil";
import {
  taskEditorTasksByTaskIdAtom,
  taskEditorStoreHydratedAtom,
} from "recoil/taskEditorStoreAtom";
import { userAtom } from "recoil/userAtom";

const TASK_EDITOR_HYDRATE_MAX_RETRY_COUNT = 5;
const TASK_EDITOR_HYDRATE_RETRY_BASE_DELAY_MS = 1000;
const TASK_EDITOR_HYDRATE_RETRY_MAX_DELAY_MS = 30000;

function getTaskEditorHydrateRetryDelayMs(retryCount: number) {
  const exponentialDelay = Math.min(
    TASK_EDITOR_HYDRATE_RETRY_BASE_DELAY_MS * 2 ** Math.max(0, retryCount - 1),
    TASK_EDITOR_HYDRATE_RETRY_MAX_DELAY_MS
  );
  const jitterMs = Math.floor(exponentialDelay * 0.2 * Math.random());
  return exponentialDelay + jitterMs;
}

export default function TaskEditorStoreBootstrap() {
  const user = useRecoilValue(userAtom);
  // 로그인 세션 식별자. 코치는 토큰을 userAtom 에 두지 않으므로 accessToken 대신 이걸 본다.
  const sessionKey = isLoggedIn(user) ? `${user?.userId}` : undefined;
  const setTasksByTaskId = useSetRecoilState(taskEditorTasksByTaskIdAtom);
  const setIsStoreHydrated = useSetRecoilState(taskEditorStoreHydratedAtom);
  const { isStoreHydrated, isStoreLoading, hydrateTaskEditorStore } =
    useTaskEditorStore();
  const previousAccessTokenRef = useRef<string | undefined>(undefined);
  const retryCountRef = useRef(0);
  const retryTimerRef = useRef<number | undefined>(undefined);
  const isHydrateInFlightRef = useRef(false);

  const clearRetryTimer = useCallback(() => {
    if (retryTimerRef.current === undefined) {
      return;
    }

    window.clearTimeout(retryTimerRef.current);
    retryTimerRef.current = undefined;
  }, []);

  const resetRetryState = useCallback(() => {
    clearRetryTimer();
    retryCountRef.current = 0;
    isHydrateInFlightRef.current = false;
  }, [clearRetryTimer]);

  const runHydrateWithRetry = useCallback(async () => {
    if (!sessionKey || isStoreHydrated || isHydrateInFlightRef.current) {
      return;
    }

    isHydrateInFlightRef.current = true;

    try {
      await hydrateTaskEditorStore();
      retryCountRef.current = 0;
      clearRetryTimer();
    } catch (error) {
      setTasksByTaskId({});
      setIsStoreHydrated(false);

      if (retryCountRef.current >= TASK_EDITOR_HYDRATE_MAX_RETRY_COUNT) {
        return;
      }

      retryCountRef.current += 1;
      const nextDelayMs = getTaskEditorHydrateRetryDelayMs(
        retryCountRef.current
      );

      clearRetryTimer();
      retryTimerRef.current = window.setTimeout(() => {
        retryTimerRef.current = undefined;
        runHydrateWithRetry().catch(() => {});
      }, nextDelayMs);
    } finally {
      isHydrateInFlightRef.current = false;
    }
  }, [
    clearRetryTimer,
    hydrateTaskEditorStore,
    isStoreHydrated,
    setIsStoreHydrated,
    setTasksByTaskId,
    sessionKey,
  ]);

  useEffect(() => {
    const currentAccessToken = sessionKey;

    if (previousAccessTokenRef.current !== currentAccessToken) {
      resetRetryState();
      previousAccessTokenRef.current = currentAccessToken;
    }

    if (!sessionKey) {
      resetRetryState();
      setTasksByTaskId({});
      setIsStoreHydrated(false);
      return;
    }

    if (isStoreHydrated) {
      retryCountRef.current = 0;
      clearRetryTimer();
      return;
    }

    if (
      isStoreLoading ||
      retryTimerRef.current !== undefined ||
      retryCountRef.current >= TASK_EDITOR_HYDRATE_MAX_RETRY_COUNT
    ) {
      return;
    }

    runHydrateWithRetry().catch(() => {});
  }, [
    clearRetryTimer,
    isStoreHydrated,
    isStoreLoading,
    resetRetryState,
    runHydrateWithRetry,
    setIsStoreHydrated,
    setTasksByTaskId,
    sessionKey,
  ]);

  useEffect(
    () => () => {
      clearRetryTimer();
    },
    [clearRetryTimer]
  );

  return null;
}
